import { useCallback, useContext, useMemo } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from 'react-query';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import * as api from '../../services/api';
import { QUERY_CACHE_TIME } from '../../constants';
import { CONFIRMABLE_LEAVE_ERRORS } from '../../constants/leave';
import useAccount from '../account/useAccount';
import useToaster from '../useToaster';

const LEDGER_PAGE_SIZE = 50;

const useEmployeeLeaveManagement = ({ employeeId }) => {
  const { t } = useContext(ConfigContext);
  const { accountId } = useAccount();
  const queryClient = useQueryClient();
  const { addSuccess, addResponseError } = useToaster();

  const managementKey = ['employeeLeaveManagement', accountId, employeeId];
  const historyKey = ['employeeLeaveHistory', accountId, employeeId];

  const { isLoading: isLoadingManagement, data: managementData } = useQuery(
    managementKey,
    () => api.fetchEmployeeLeaveManagement({ accountId, employeeId }),
    {
      enabled: !!accountId && !!employeeId,
      cacheTime: QUERY_CACHE_TIME,
      refetchOnMount: 'always'
    }
  );

  const {
    isLoading: isLoadingHistory,
    isFetchingNextPage: isLoadingMoreHistory,
    data: historyData,
    fetchNextPage: fetchMoreHistory,
    hasNextPage: hasMoreHistory
  } = useInfiniteQuery(
    historyKey,
    ({ pageParam = 0 }) =>
      api.fetchEmployeeLeaveHistory({
        accountId,
        employeeId,
        ledgerLimit: LEDGER_PAGE_SIZE,
        ledgerOffset: pageParam
      }),
    {
      enabled: !!accountId && !!employeeId,
      cacheTime: QUERY_CACHE_TIME,
      refetchOnMount: 'always',
      getNextPageParam: (lastPage, allPages) => {
        const total = lastPage?.data?.ledgerTotal;
        if (total === undefined) return undefined;
        const totalSoFar = allPages.reduce((sum, page) => sum + (page?.data?.ledger?.length || 0), 0);
        return totalSoFar < total ? totalSoFar : undefined;
      }
    }
  );

  // Records come from page 0 only — server returns the full list on every page, no need to merge.
  const history = useMemo(() => {
    const firstPage = historyData?.pages?.[0]?.data;
    if (!firstPage) return null;
    return {
      ...firstPage,
      ledger: historyData.pages.flatMap((page) => page?.data?.ledger ?? [])
    };
  }, [historyData]);

  const fetchFullLedger = useCallback(async () => {
    const { data } = await api.fetchEmployeeLeaveHistory({ accountId, employeeId });

    return data?.ledger || [];
  }, [accountId, employeeId]);

  const invalidateAll = () => {
    queryClient.invalidateQueries(managementKey);
    queryClient.invalidateQueries(historyKey);
  };

  const mutationOptions = (successKey) => ({
    onSuccess: () => {
      if (successKey) addSuccess(t(successKey));
      invalidateAll();
    },
    onError: addResponseError
  });

  // adminId is server-derived; BE schema strips it on assign.
  const { mutateAsync: assignContract } = useMutation(
    (params) => api.assignEmployeeLeaveContract({ accountId, employeeId, ...params }),
    mutationOptions('leaveContractAssignedSuccessfully')
  );

  const { mutateAsync: updateAssignment } = useMutation(
    ({ assignmentId, ...params }) =>
      api.updateEmployeeLeaveContractAssignment({ accountId, employeeId, assignmentId, ...params }),
    mutationOptions('leaveAssignmentUpdatedSuccessfully')
  );

  const { mutateAsync: terminateAssignment } = useMutation(
    ({ assignmentId, ...params }) =>
      api.terminateEmployeeLeaveContractAssignment({ accountId, employeeId, assignmentId, ...params }),
    mutationOptions('leaveAssignmentTerminatedSuccessfully')
  );

  const { mutateAsync: changeContract } = useMutation(
    (params) => api.changeEmployeeLeaveContract({ accountId, employeeId, ...params }),
    mutationOptions('leaveContractChangedSuccessfully')
  );

  const { mutateAsync: yearReset } = useMutation(
    ({ assignmentId, ...params }) => api.yearResetEmployeeLeave({ accountId, employeeId, assignmentId, ...params }),
    mutationOptions('leaveYearResetSuccessfully')
  );

  // Pays out without terminating — distinct from terminate+payoutRemainder.
  const { mutateAsync: payoutAllBalances } = useMutation(
    ({ assignmentId, ...params }) =>
      api.payoutAllEmployeeLeaveBalances({ accountId, employeeId, assignmentId, ...params }),
    mutationOptions('employeeLeavePayoutAllSuccessfully')
  );

  const { mutateAsync: adjustBalance } = useMutation(
    (params) => api.adjustEmployeeLeaveBalance({ accountId, employeeId, ...params }),
    mutationOptions('leaveBalanceAdjustedSuccessfully')
  );

  const { mutateAsync: createLeaveRecord } = useMutation(
    (params) => api.createEmployeeLeaveRecord({ accountId, employeeId, ...params }),
    {
      onSuccess: () => {
        addSuccess(t('leaveRecordCreatedSuccessfully'));
        invalidateAll();
      },
      onError: (error) => {
        const errorData = error?.response?.data;
        const code = errorData?.errorCode;
        if (!CONFIRMABLE_LEAVE_ERRORS.includes(code)) {
          addResponseError(error);
        }
      }
    }
  );

  const { mutateAsync: cancelLeaveRecord } = useMutation(
    ({ recordId }) => api.removeLeaveRecordForPayrollQuery({ accountId, leaveRecordId: recordId }),
    mutationOptions('leaveRecordCancelledSuccessfully')
  );

  const fetchExpectedPay = useCallback(
    (params) => api.fetchEmployeeLeaveExpectedPay({ accountId, employeeId, ...params }),
    [accountId, employeeId]
  );

  return {
    isLoadingManagement,
    isLoadingHistory,
    isLoadingMoreHistory,
    management: managementData?.data || null,
    history,
    fetchMoreHistory,
    fetchFullLedger,
    hasMoreHistory: !!hasMoreHistory,
    assignContract,
    updateAssignment,
    terminateAssignment,
    changeContract,
    yearReset,
    payoutAllBalances,
    adjustBalance,
    createLeaveRecord,
    cancelLeaveRecord,
    fetchExpectedPay
  };
};

export default useEmployeeLeaveManagement;
